import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';
import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { getEndDatetime, getStartDatetime } from '@/helpers/showings-helper';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useProgramFilterContext } from '@/providers/ProgramFilterProvider';
import { dateTodayPlus, logicalStartOfToday } from '@/utils/date-helper';

import { useFutureShowings } from './useFutureShowings';

type UseShowingsListFilteredOptions = {
    selectedDate?: string;
    showingTagId?: ShowingTagDto['id'];
    contentId?: ContentDto['id'];
    group?: ShowingApiGroup;
    keepPreviousData?: boolean;
};

type UseShowingsListFilteredParams = UseShowingsListFilteredOptions &
    (
        | {
              initialShowings?: never;
              initialShowingsUpdatedAt?: never;
          }
        | {
              /** Raw, unfiltered `showings-future` response of the cinema, fetched on the server. */
              initialShowings: ShowingDto[];
              initialShowingsUpdatedAt: number;
          }
    );

function useShowingsListFiltered({
    initialShowings,
    initialShowingsUpdatedAt,
    ...options
}: UseShowingsListFilteredParams) {
    const domainConfiguration = useDomainConfiguration();
    const { activeFilters } = useProgramFilterContext();

    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;
    const selectedDate =
        options.selectedDate === 'all'
            ? logicalStartOfToday()
            : options.selectedDate === 'soon'
              ? dateTodayPlus(14)
              : new Date(options.selectedDate ?? '');

    const getShowingsOptions = {
        filter: activeFilters,
        startDate: options.selectedDate ? selectedDate : logicalStartOfToday(),
        cinemaId: cinemaId
    };
    const startDatetime = getStartDatetime(getShowingsOptions);
    const endDatetime =
        options.selectedDate && !['all', 'soon'].includes(options.selectedDate)
            ? getEndDatetime(getShowingsOptions)
            : null;

    const showingTagIds =
        activeFilters.showingTagIds && Array.isArray(activeFilters.showingTagIds)
            ? activeFilters.showingTagIds
            : options.showingTagId
              ? [options.showingTagId]
              : [];

    const { data: showings, isLoading: isFetchingShowingList } = useFutureShowings(
        cinemaId,
        {
            contentId: options.contentId,
            group: options.group,
            ...activeFilters,
            startDatetime,
            endDatetime: endDatetime ?? undefined
        },
        showingTagIds.length > 0
            ? {
                  showingTagIds
              }
            : null,
        {
            // initialShowings seeds the raw (unfiltered) request cache; it only matches that cache
            // entry when no server-side query narrows the response.
            ...(initialShowings &&
                showingTagIds.length === 0 && {
                    initialData: initialShowings,
                    initialDataUpdatedAt: initialShowingsUpdatedAt
                }),
            ...(options.keepPreviousData !== undefined && { keepPreviousData: options.keepPreviousData })
        }
    );

    const totalItems = showings?.length ?? 0;
    // Only report "no showings" once the list has actually resolved — `showings` defaults to
    // an (truthy) empty array while loading, which previously rendered a false empty state
    // in the server HTML before the filtered data was available.
    const noShowingsForSelectedCinemas: boolean = !isFetchingShowingList && totalItems === 0;

    return {
        showings,
        isFetchingShowingList,
        noShowingsForSelectedCinemas,
        totalItems
    };
}

export default useShowingsListFiltered;
